/* eslint-disable react/no-unstable-nested-components */
import { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { getDeerEngagementSurveyQuestionnaireRoute } from 'Deer/constants/nonDeerRoutes';
import { useCreateNewSurveyForm } from 'Deer/pages/Company/Engagement/Surveys/SurveyCreation/dataHook';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useEngagementActiveSurveyData } from 'screens/Engagement/LandingPageV2/hooks/dataHooks';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getSurveyRedirectionRoute } from 'screens/admin/Engagement';
import { Loader } from 'screens/analytics/commonComponents';
import { checkEnableCompanySupport, checkEnableNetchexEmployeeNavbar } from 'utils/helpers/featureFlags';
import { checkUserIsPlatformAdmin } from 'utils/helpers/rolesAccess';

import { Plus } from 'assets/assetsV1/iconComponents';
import engagementNoDataImageV1 from 'assets/assetsV1/illustrations/newIllustrations/engagementNoDataImageV1.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey, getPulseSurveyRoute } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendStartedEngagementSurvey } from 'constants/mixpanelHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

const getBackground = (status) => {
  if (status === 'NOT_STARTED') return { background: '#EBEFF3', color: '#333333', text: 'Not Started' };
  return { background: '#FFEFD5', color: '#8C6A00', text: 'In-progress' };
};

const CreateNewSurveyButton = () => {
  const history = useHistory();
  const { mutate: createNewSurveyForm } = useCreateNewSurveyForm();

  const createNewSurvey = () => {
    const isSurveyCreationRevamp = checkEnableCompanySupport();
    if (isSurveyCreationRevamp) {
      createNewSurveyForm();
      return;
    }

    const route = getSurveyRedirectionRoute({ isNew: true });
    history.push(route);
  };

  return (
    <Button onClick={createNewSurvey} StartIcon={Plus}>
      Create New Survey
    </Button>
  );
};

const ActiveSurveysIntentTable = ({ gridRef }) => {
  const isUserAdmin = checkUserIsPlatformAdmin();

  const history = useHistory();

  const { data, isLoading } = useEngagementActiveSurveyData({
    limit: 100,
    offset: 0,
    includeCount: true,
  });

  const { surveys: surveysList } = data ?? {};

  const onStart = (data) => {
    sendStartedEngagementSurvey({
      eventProperties: {
        surveyId: data?.data?.id,
      },
    });

    if (isMobileView() && !checkEnableNetchexEmployeeNavbar()) {
      history.push(getPulseSurveyRoute(data?.data?.id));
      return;
    }
    const newRoute = getDeerEngagementSurveyQuestionnaireRoute(data?.data?.id);

    history.push(newRoute);
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      suppressHeaderMenuButton: true,
      headerComponent: CustomHeaderCell,
      minWidth: 200,
      flex: 1,
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
      headerComponentParams: {
        enableMenu: true,
        enableSorting: true,
      },
      menuTabs: ['filterMenuTab'],
    }),
    []
  );
  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const mobileColumnDefs = useMemo(
    () => [
      {
        headerName: 'Survey',
        field: 'name',
        // eslint-disable-next-line no-shadow
        cellRenderer: ({ data }) => {
          const parsedDate = data?.endDate?.value
            ? format(parseISO(data?.endDate?.value?.split('T')[0]), 'dd MMM yyyy')
            : '—';
          const { background, text } = getBackground(data?.submissionStatus) ?? {};

          return (
            <Flex flexDirection="column">
              <TextBodyMedium>{data?.name}</TextBodyMedium>
              <Flex alignItems="center">
                <TextBodyMedium mr="8px">Due: {parsedDate}</TextBodyMedium>
                <Box p="2px 8px" bg={background} width="fit-content" borderRadius="4px">
                  <TextBodyMedium bold>{text}</TextBodyMedium>
                </Box>
              </Flex>
            </Flex>
          );
        },
        flex: 1,
        minWidth: 280,
        // align: 'left',
        filter: 'agTextColumnFilter',
      },
    ],
    []
  );

  const columnDefs = useMemo(() => {
    const COLUMNS = [
      {
        headerName: 'Survey',
        field: 'name',
        cellRenderer: ({ value }) => <TextBodyMedium>{value}</TextBodyMedium>,
        width: 'auto',
        align: 'left',
        filter: 'agTextColumnFilter',
      },
      // {
      //   headerName: 'Drivers',
      //   field: 'surveyDrivers',
      //   cellRenderer: ({ value = [] }) => {
      //     const driversToShow = value.slice(0, 2);
      //     const remainingDriversCount = value.length > 2 ? `+${value.length - 2}` : '';

      //     return (
      //       <TextBodyMedium>
      //         {driversToShow.join(', ')} {remainingDriversCount}
      //       </TextBodyMedium>
      //     );
      //   },
      //   filter: 'agSetColumnFilter',
      // },
      {
        headerName: 'Due Date',
        field: 'endDate',
        valueGetter: ({ data }) => data?.endDate?.value,
        filterValueGetter: ({ data }) => parseISO(data?.endDate?.value.split('T')[0]),
        cellRenderer: ({ value }) => {
          const parsedDate = value ? format(parseISO(value.split('T')[0]), 'dd MMM yyyy') : '—';
          return <TextBodyMedium>{parsedDate}</TextBodyMedium>;
        },
        filter: 'agDateColumnFilter',
      },
      {
        headerName: 'Survey Status',
        field: 'submissionStatus',
        cellRenderer: ({ value }) => {
          const { background, text } = getBackground(value) ?? {};
          return (
            <Box p="2px 8px" bg={background} width="fit-content" borderRadius="4px">
              <TextBodyMedium bold>{text}</TextBodyMedium>
            </Box>
          );
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['NOT_STARTED', 'IN_PROGRESS'],
          valueFormatter: ({ value }) => getBackground(value).text,
        },
      },
    ];
    return COLUMNS;
  }, []);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.ENGAGEMENT;
    const viewId = 'My_Engagement';
    const tabId = 'ActiveSurveys';
    const uniqueId = 'Engagement_Active_Surveys';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!surveysList?.length) {
    return (
      <div style={{ marginTop: '60px' }}>
        {isUserAdmin ? (
          <EmptyScreen
            heading="Good job!"
            description="You have responded to all active surveys."
            illustrationSrc={engagementNoDataImageV1}
            CTAComp={isMobileView() ? null : CreateNewSurveyButton}
          />
        ) : (
          <EmptyScreen
            heading="Good job!"
            description="You have responded to all active surveys."
            illustrationSrc={engagementNoDataImageV1}
          />
        )}
      </div>
    );
  }

  return (
    <AgGridTable
      defaultColDef={defaultColDef}
      gridStatePersistenceKey={gridStatePersistenceKey}
      ref={gridRef}
      rowData={surveysList}
      columnDefs={isMobileView() ? mobileColumnDefs : columnDefs}
      animateRows={false}
      domLayout="autoHeight"
      pagination
      headerHeight={36}
      rowHeight={isMobileView() ? 62 : 52}
      autoHeight={false}
      sideBar={sideBar}
      onRowClicked={onStart}
      containerAutoHeight
    />
  );
};

export { ActiveSurveysIntentTable };
